import { useEffect, useState, useRef } from "react";
import { Button, Typography, Card, Box, TextField, Grid } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "src/@core/components/page-header";
import Translations from "src/layouts/components/Translations";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { DefaultPaginationSettings } from "src/constants/general.const";
import { toastError, toastSuccess } from "src/utils/utils";
import TableLessons from "src/views/tables/TableLessons";
import DialogAddLesson from "src/views/dialog/DialogAddLesson";
import DialogConfirmation from "src/views/dialog/DialogConfirmation";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from "src/hooks/useAuth";
import { canCreate, canUpdate, canRemove } from "src/utils/permissions";

const LessonPage = () => {
  const { givenPermission, user } = useAuth();
  const { moduleId } = useParams();
  const searchTimeoutRef = useRef();
  const [loading, setLoading] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [search, setSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(
    DefaultPaginationSettings.ROWS_PER_PAGE
  );
  const [lessonDialogOpen, setLessonDialogOpen] = useState(false);
  const [lessonToEdit, setLessonToEdit] = useState(null);
  const [dialogMode, setDialogMode] = useState("add");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState(null);

  // Check course permissions (lessons inherit course permissions)
  const canCreateLesson = canCreate(givenPermission, "/courses", user?.user_type);
  const canUpdateLesson = canUpdate(givenPermission, "/courses", user?.user_type);
  const canDeleteLesson = canRemove(givenPermission, "/courses", user?.user_type);

  const toggleConfirm = (e, lesson = null) => {
    setConfirmOpen((prev) => !prev);
    setLessonToDelete(lesson);
  };

  const handleAddLesson = () => {
    setDialogMode("add");
    setLessonToEdit(null);
    setLessonDialogOpen(true);
  };

  const handleEditLesson = (e, lesson) => {
    setDialogMode("edit");
    setLessonToEdit(lesson);
    setLessonDialogOpen(true);
  };

  const handleLessonDialogClose = () => {
    setLessonDialogOpen(false);
    setLessonToEdit(null);
  };

  const fetchData = ({ currentPage, pageSize, search }) => {
    setLoading(true);
    const params = { page: currentPage, limit: pageSize, search };

    axiosInstance
      .get(ApiEndPoints.LESSONS.list(moduleId), { params })
      .then((response) => {
        setLessons(response.data?.data?.lessons || []);
        setTotalCount(response.data?.data?.totalRecords || 0);
      })
      .catch((error) => toastError(error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (moduleId) {
      fetchData({ currentPage, pageSize, search });
    }
  }, [currentPage, pageSize, search, moduleId]);

  const handleSearchChange = (e) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setSearch(e.target.value);
      setCurrentPage(1);
    }, 500);
  };

  const onConfirmDelete = () => {
    axiosInstance
      .delete(ApiEndPoints.LESSONS.delete(lessonToDelete?.id))
      .then((response) => {
        toastSuccess(response.data?.message || "Lesson deleted successfully");
        fetchData({ currentPage, pageSize, search });
        setConfirmOpen(false);
        setLessonToDelete(null);
      })
      .catch((error) => {
        toastError(error);
      });
  };

  return (
    <>
      <Grid container spacing={4} padding={2}>
        <Grid item xs={12}>
          <Box sx={{
            p: 5,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}>
            <Box onClick={() => navigate(-1)} sx={{ cursor: "pointer" }}>
              <ArrowBackIcon />
            </Box>
            <PageHeader
              title={
                <Typography variant="h5">
                  <Translations text="Lessons" />
                </Typography>
              }
              action={
                canCreateLesson ? (
                  <Button
                    variant="contained"
                    onClick={handleAddLesson}
                  >
                    Add Lesson
                  </Button>
                ) : null
              }
            />
          </Box>
          <Card>
            <Box
              sx={{
                p: 5,
                pb: 0,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Box
                sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}
              >
                <TextField
                  type="search"
                  size="small"
                  placeholder="Search"
                  onChange={handleSearchChange}
                />
              </Box>
            </Box>
            <Box sx={{ p: 5 }}>
              <TableLessons
                rows={lessons}
                loading={loading}
                totalCount={totalCount}
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                setPageSize={setPageSize}
                pageSize={pageSize}
                toggleEdit={handleEditLesson}
                toggleDelete={toggleConfirm}
                canEdit={canUpdateLesson}
                canDelete={canDeleteLesson}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>

      <DialogAddLesson
        open={lessonDialogOpen}
        onClose={handleLessonDialogClose}
        onSuccess={() => {
          fetchData({
            currentPage: currentPage,
            pageSize: pageSize,
            search: search,
          });
        }}
        mode={dialogMode}
        dataToEdit={lessonToEdit}
        moduleId={moduleId}
      />

      <DialogConfirmation
        open={confirmOpen}
        toggle={toggleConfirm}
        title="Delete Lesson"
        subtitle="Are you sure you want to delete this lesson?"
        onConfirm={onConfirmDelete}
      />
    </>
  );
};

export default LessonPage;
